import { useEffect, useRef } from 'react';

import { Meter } from '../../shared/ui/meter';
import { Note } from '../../shared/ui/note';
import { PlayIcon } from '../../shared/ui/icons';
import { useBulkOperation } from '../../entities/bulk-operation';
import { formatBytes, useRefreshStorage } from '../../entities/retention';

interface IDeleteProgressProps {
  operationId: string;
  bytes: number | null;
  onFinished: () => void;
}

export const DeleteProgress = ({ operationId, bytes, onFinished }: IDeleteProgressProps) => {
  const { operation, finished } = useBulkOperation(operationId);
  const refresh = useRefreshStorage();

  // Once per operation: `refresh` and `onFinished` are new on every render.
  const settled = useRef(false);
  useEffect(() => {
    if (!finished || settled.current) return;
    settled.current = true;
    void refresh().then(onFinished);
  }, [finished, refresh, onFinished]);

  const share =
    operation?.total && operation.total > 0
      ? Math.min(1, operation.processed / operation.total)
      : 0;

  return (
    <div className="rounded-panel border border-border-sunken p-4">
      <span className="inline-flex items-center gap-label text-data text-primary">
        <PlayIcon size="sm" className="text-muted" />
        Deleting
      </span>
      <p className="mt-2 text-data font-semibold text-primary">
        {bytes !== null
          ? `Deleted ~${formatBytes(bytes * share)} of ${formatBytes(bytes)}`
          : `Deleted ${Math.round(share * 100)}%`}
      </p>
      <Meter value={share * 100} tone="accent" className="mt-2" />
      <p className="mt-2 font-mono text-micro text-faint">
        {operationId} · runs on the server · safe to leave this page
      </p>
      {operation?.error && (
        <Note tone="danger" className="mt-2">
          {operation.error}
        </Note>
      )}
    </div>
  );
};
